<template>
  <main-template
    :header="header"
    :is-loading="isLoading"
  >
    <div class="center ph3 pv4 page-container">
      <!-- Page Header -->
      <section class="mb4 page-header">
        <h1>MANAGEMENT SERVICE</h1>
        <div
          v-html="content"
          class="mt3 f5-ns f6 lh-copy page-header-copy"
        />
      </section>

      <!-- Common Area Management Services Section -->
      <section class="mb4 mt5 service-section">
        <div class="flex justify-between service-layout">
          <div class="service-content tl">
            <h2 class="mt0 mb4 f3 fw5 lh-title tracking-normal service-title">
              COMMON AREA MANAGEMENT SERVICES
            </h2>
            <div class="f6 lh-copy pr2-l">
              <p class="mb3">
                Brown Starling Co., Ltd. providers common area management services for residential projects and condominiums.
              </p>
              <p class="mb3">
                Common area management refers to the management, maintenance, and operation of shared spaces within a residential or commercial property.
              </p>
              <p class="mb1">These common areas typically include:</p>
              <ul class="pl3 mt0 mb3">
                <li class="mb1">Lobbies and reception areas</li>
                <li class="mb1">Swimming pools and gyms</li>
                <li class="mb1">Gardens and landscaping</li>
                <li class="mb1">Corridors, elevators, and parking areas</li>
                <li class="mb1">Shared utilities (lighting, water systems, security)</li>
              </ul>
              <p class="mb4">
                Common area management usually covers services such as cleaning, maintenance, repairs, security, utility management, and overall upkeep, ensuring that all shared facilities are safe, functional, and well-maintained for residents or owners.
              </p>
              <div
                class="dib f6 dim pointer"
                @click.prevent="redirectTo('https://brownstarling.com')"
              >
                <span class="learn-more-text">Learn More</span>
                <span class="arrow">→</span>
              </div>
            </div>
          </div>

          <div class="service-media mobile-order-first">
            <div class="aspect-ratio-mobile">
              <hg-carousel
                type="image"
                :image-list="commonAreaImageList"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Villa Management Services Section -->
      <section class="mb4 mt5 service-section">
        <div class="flex justify-between service-layout">
          <div class="service-media mobile-order-first">
            <div class="aspect-ratio-mobile">
              <hg-carousel
                type="image"
                :image-list="villaImageList"
              />
            </div>
          </div>

          <div class="service-content tl">
            <h2 class="mt0 mb4 f3 fw5 lh-title tracking-normal service-title">
              VILLA MANAGEMENT SERVICES
            </h2>
            <div class="f6 lh-copy pr2-l">
              <p class="mb3">
                Villa management service refers to a professional service that oversees, operates, and maintains a villa on behalf of the owner, ensuring the property is well cared for and functions smoothly.
              </p>
              <p class="mb3">
                Villa management services typically include:
              </p>
              <ul class="pl3 mt0 mb4">
                <li class="mb1">Property maintenance and repairs</li>
                <li class="mb1">Housekeeping and cleaning services</li>
                <li class="mb1">Utility and bill management</li>
                <li class="mb1">Garden and pool maintenance</li>
                <li class="mb1">Rental management and coordination</li>
                <li class="mb1">Owner reporting and administrative support</li>
              </ul>
              <p class="ma0 mb4">
                This service is especially useful for villa owners who do not live on-site, as it helps maintain the villa's condition, value, and overall guest or resident experience.
              </p>
            </div>
            <div
              class="dib f6 dim pointer"
              @click.prevent="redirectTo('https://brownstarling.com')"
            >
              <span class="learn-more-text">Learn More</span>
              <span class="arrow">→</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main-template>
</template>

<script>
import _ from 'lodash'
import { COVER_IMG_TYPE, HERO_TEXT_CONFIG_KEY, SERVICE_KEY, MANAGEMENT_SERVICES_CONST } from '@/configs/constants'
import ConfigApi from '@/api/config-api'
import AlertMixin from '@/mixins/alert-mixin'
import MainTemplate from '@/templates/MainTemplate'
import HgCarousel from '@/components/Carousel'

export default {
  name: 'ManagementServices',
  mixins: [AlertMixin],
  components: { MainTemplate, HgCarousel },
  data () {
    return {
      isLoading: false,
      header: {
        className: '',
        style: '',
        imgPath: '',
        isShow: true,
        resizeLogo: true,
        heroText: {
          headline: '',
          subHeadline: ''
        }
      },
      content: '',
      commonAreaImageList: MANAGEMENT_SERVICES_CONST.COMMON_AREA_IMAGE_LIST,
      villaImageList: MANAGEMENT_SERVICES_CONST.VILLA_IMAGE_LIST
    }
  },
  async created () {
    this.isLoading = true

    try {
      const promiseList = [
        ConfigApi.coverImgUrl(COVER_IMG_TYPE.MANAGEMENT_SERVICES),
        ConfigApi.configByKey(SERVICE_KEY.MANAGEMENT_SERVICES),
        ConfigApi.configByKey(HERO_TEXT_CONFIG_KEY.MANAGEMENT_SERVICES)
      ]

      const responseList = await Promise.all(promiseList)
      const coverImage = responseList[0]
      const heroTextValue = _.get(responseList, '[2].configValue', '{}')

      this.header.imgPath = coverImage.url
      this.content = _.get(responseList, '[1].configValue', '')
      this.header.heroText = JSON.parse(heroTextValue)
    } catch (error) {
      this.alertMessageError(error.message)
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    redirectTo (link) {
      window.open(link, '_blank')
    }
  }
}
</script>

<style scoped>
.page-container {
  max-width: 1100px;
}

.page-header-copy {
  max-width: 900px;
}

.service-title {
  max-width: 260px;
}

.service-section {
  margin-bottom: 20px;
}

.service-layout {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.service-content {
  width: 100%;
  max-width: 450px;
  margin: 0 0 1rem;
}

.service-media {
  width: 100%;
  max-width: 450px;
  margin-bottom: 20px;
}

.mobile-order-first {
  order: -1;
  position: relative;
}

.aspect-ratio-mobile {
  position: relative;
  aspect-ratio: 3 / 2;
  overflow: hidden;
  height: auto;
  width: 100%;
}

.mobile-order-first .aspect-ratio-mobile {
  position: relative;
  inset: auto;
  overflow: hidden;
}

.learn-more-text {
  text-decoration: underline;
  text-underline-offset: 4px;
  color: #6A7B83; /* สีเทาอ่อน */
}

.arrow {
  margin: 0 0 0 8px;
}

@media (min-width: 992px) {
  .page-header {
    margin-bottom: 3rem;
  }

  .service-layout {
    flex-direction: row;
    align-items: stretch;
  }

  .service-section {
    margin-bottom: 4rem;
  }

  .service-content {
    width: 40%;
    padding-right: 4rem;
    margin: 0;
  }

  .service-media {
    width: 50%;
    margin-bottom: 0;
  }

  .mobile-order-first {
    order: unset;
    position: relative;
  }

  .aspect-ratio-mobile {
    position: static;
    aspect-ratio: auto;
    overflow: visible;
    height: 100%;
  }

  .mobile-order-first .aspect-ratio-mobile {
    position: absolute;
    inset: 0;
    overflow: hidden;
  }
}
</style>
